import { apiRequest } from '@/shared/api/http'

export interface CurrentUser {
  id: string
  userName: string
  displayName: string
  email: string
  roles: string[]
  sessionVersion: number
}

export interface LoginRequest {
  identifier: string
  password: string
  rememberMe?: boolean
}

export interface RegistrationChallengeRequest {
  inviteCode: string
  email: string
}

export interface RegistrationChallengeResponse {
  challengeId: string
  maskedEmail: string
  expiresAt: string
  resendAt: string
  serverNow: string
}

export interface RegisterRequest {
  challengeId: string
  verificationCode: string
  userName: string
  displayName: string
  password: string
}

export interface CompletePasswordResetRequest {
  ticketId: string
  secret: string
  newPassword: string
}

export const authApi = {
  me(signal?: AbortSignal): Promise<CurrentUser> {
    return apiRequest<CurrentUser>('/api/auth/me', signal ? { signal } : {})
  },

  login(request: LoginRequest): Promise<CurrentUser | undefined> {
    return apiRequest<CurrentUser | undefined>('/api/auth/login', {
      method: 'POST',
      body: request,
    })
  },

  logout(): Promise<void> {
    return apiRequest<void>('/api/auth/logout', { method: 'POST' })
  },

  createRegistrationChallenge(
    request: RegistrationChallengeRequest,
  ): Promise<RegistrationChallengeResponse> {
    return apiRequest<RegistrationChallengeResponse>(
      '/api/auth/registration-email-challenges',
      { method: 'POST', body: request },
    )
  },

  resendRegistrationChallenge(challengeId: string): Promise<RegistrationChallengeResponse> {
    return apiRequest<RegistrationChallengeResponse>(
      `/api/auth/registration-email-challenges/${encodeURIComponent(challengeId)}/resend`,
      { method: 'POST' },
    )
  },

  cancelRegistrationChallenge(challengeId: string): Promise<void> {
    return apiRequest<void>(
      `/api/auth/registration-email-challenges/${encodeURIComponent(challengeId)}/cancel`,
      { method: 'POST' },
    )
  },

  register(request: RegisterRequest): Promise<CurrentUser | undefined> {
    return apiRequest<CurrentUser | undefined>('/api/auth/register', {
      method: 'POST',
      body: request,
    })
  },

  requestPasswordReset(email: string): Promise<void> {
    return apiRequest<void>('/api/auth/password-reset-requests', {
      method: 'POST',
      body: { email },
    })
  },

  completePasswordReset(request: CompletePasswordResetRequest): Promise<void> {
    return apiRequest<void>('/api/auth/reset-password', {
      method: 'POST',
      csrf: false,
      body: request,
    })
  },
}
